import Card from "@/components/common/Card";
import FormField from "@/components/common/FormField";
import MainHeading from "@/components/common/MainHeading";
import Layout from "@/components/layout/Layout";
import { Button } from "@/components/ui/button";
import { LeaveService } from "@/services/Leave/LeaveSetting";
import { showToastNotification } from "@/utility/helper";
import { RoutesLink } from "@/utility/RouteLinks";
import { errorTypes } from "@/utility/validation";
import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { ThrowError } from "@/api/callApi";
import type {
  ILeave,
  ILeaveOverview,
  ILeaveResponse,
  ILeaveSetting,
} from "@/interface/leave";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import FullPageLoader from "@/components/common/FullPageLoader";
import moment from "moment";

const AddLeave: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const { userId, siteId } = useCurrentUser();
  const [isLoading, setIsLoading] = useState(false);
  const [isFatchLoading, setIsFatchLoading] = useState(false);
  const [leaveTypes, setLeaveTypes] = useState<
    { value: string; label: string }[]
  >([]);
  // const [isLeaveTypesLoaded, setIsLeaveTypesLoaded] = useState(false);
  // const [shifts, setShifts] = useState<{ value: string; label: string }[]>([]);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    clearErrors,
    trigger,
    formState: { errors },
  } = useForm<ILeave>({
    mode: "onChange",
    defaultValues: {
      leaveType: "",
      duration: "",
      startDate: "",
      endDate: "",
      numberOfDays: 0,
      reason: "",
      shiftId: "",
    },
  });

  const state = location.state as { leaveBalances?: ILeaveOverview } | null;
  const [leaveBalances, setLeaveBalances] = useState<ILeaveOverview | null>(
    state?.leaveBalances || null,
  );
  // const [isOverviewLoading, setIsOverviewLoading] = useState(false);

  const startDate = watch("startDate");
  const endDate = watch("endDate");
  const duration = watch("duration");
  const selectedLeaveType = watch("leaveType");

  useEffect(() => {
    if (state?.leaveBalances) {
      setLeaveBalances(state.leaveBalances);
    }
  }, [state?.leaveBalances]);

  useEffect(() => {
    if (!leaveBalances && userId && siteId) {
      const fetchOverview = async () => {
        // setIsFatchLoading(true);
        try {
          const response = await LeaveService.getLeaveOverview(userId, siteId);
          if (response?.status && response?.data) {
            setLeaveBalances(response.data);
          }
        } catch (error) {
          console.error("Error fetching leave overview:", error);
        }
        // finally {
        //   setIsFatchLoading(false);
        // }
      };
      fetchOverview();
    }
  }, [userId, siteId, leaveBalances]);

  const selectedBalance = leaveBalances?.leaves?.find(
    (l) => l.type === selectedLeaveType,
  );

  const availableBalance = selectedBalance?.available ?? 0;


  const getLeaveTypes = async () => {
    setIsFatchLoading(true);
    try {
      const response = await LeaveService.getLeaveSettings();
      if (response?.status) {
        const settings = response?.data?.leaveSettings;
        const formatted = (settings || [])?.map((item: ILeaveSetting) => ({
          value: item.type || "",
          label: item.type,
        }));
        if (formatted.length > 0) {
          setLeaveTypes(formatted);
        } else {
          setLeaveTypes([
            { value: "Earned Leave", label: "Earned Leave" },
            { value: "Casual Leave", label: "Casual Leave" },
            { value: "Sick Leave", label: "Sick Leave" },
          ]);
        }
      }
    } catch (error) {
      ThrowError(error);
      setLeaveTypes([
        { value: "Earned Leave", label: "Earned Leave" },
        { value: "Casual Leave", label: "Casual Leave" },
        { value: "Sick Leave", label: "Sick Leave" },
      ]);
    } finally {
      setIsFatchLoading(false);
    }
  };

  const getLeaveById = async () => {
    if (!id) return;
    setIsFatchLoading(true);
    try {
      const response = await LeaveService.getLeaveById(id);
      if (response?.status && response.data) {
        const data = response.data;
        setValue("leaveType", data.leaveType);
        setValue("startDate", data.startDate ? data.startDate.split("T")[0] : "",);
        setValue("endDate", data.endDate ? data.endDate.split("T")[0] : "");
        setValue("duration", data.duration);
        setValue("numberOfDays", data.numberOfDays);
        setValue("reason", data.reason);
        setValue("shiftId", data.shiftId || "");
      }
    } catch (error) {
      ThrowError(error);
    } finally {
      setIsFatchLoading(false)
    }

  };

  useEffect(() => {
    getLeaveTypes();
  }, []);

  useEffect(() => {
    if (id && isFatchLoading) {
      getLeaveById();
    }
  }, [id, isFatchLoading]);


  // useEffect(() => {
  //   if (duration === "FIRST_HALF" || duration === "SECOND_HALF") {
  //     setValue("numberOfDays", 0.5);
  //     if (startDate) {
  //       setValue("endDate", startDate);
  //     }
  //   } else if (startDate && endDate) {
  //     const start = new Date(startDate);
  //     const end = new Date(endDate);
  //     const diffTime = end.getTime() - start.getTime();
  //     const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
  //     setValue("numberOfDays", diffDays > 0 ? diffDays : 1);
  //   }
  // }, [startDate, endDate, duration, setValue]);

  useEffect(() => {
    if (duration === "FIRST_HALF" || duration === "SECOND_HALF") {
      setValue("numberOfDays", 0.5, {
        shouldValidate: true,
        shouldDirty: true,
      });

      clearErrors("numberOfDays");

      if (startDate) {
        setValue("endDate", startDate, {
          shouldValidate: true,
          shouldDirty: true,
        });
      }
    } else if (startDate && endDate) {
      const start = new Date(startDate);
      const end = new Date(endDate);

      const diffTime = end.getTime() - start.getTime();
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;

      setValue("numberOfDays", diffDays > 0 ? diffDays : 1, {
        shouldValidate: true,
        shouldDirty: true,
      });

      clearErrors("numberOfDays");
    }
  }, [startDate, endDate, duration, setValue, clearErrors]);

  useEffect(() => {
    if (startDate && endDate) {
      if (new Date(endDate) < new Date(startDate)) {
        setValue("endDate", startDate, {
          shouldValidate: true,
          shouldDirty: true,
        });
      } else {
        trigger("endDate");
      }
    }
  }, [startDate, endDate, setValue, trigger]);

  const onSubmit = async (data: ILeave) => {
    if (!userId) {
      showToastNotification("danger", "User not logged in");
      return;
    }

    try {
      setIsLoading(true);
      const payload = {
        userId: userId,
        siteId: siteId,
        startDate: data.startDate || watch("startDate"),
        endDate: data.endDate || watch("endDate"),
        leaveType: data.leaveType,
        duration: data.duration,
        numberOfDays: Number(data.numberOfDays) || watch("numberOfDays"),
        reason: data.reason,
        // shiftId: data.shiftId || undefined,
      };

      let response: ILeaveResponse;
      if (id) {
        response = await LeaveService.updateLeave(payload, id); // Fallback to update call
      } else {
        response = await LeaveService.createLeave(payload);
      }

      if (response?.status) {
        showToastNotification(
          "success",
          response?.message || "Leave applied successfully",
        );
        navigate(RoutesLink.myLeave);
      } else {
        showToastNotification(
          "danger",
          response?.message || "Failed to submit leave",
        );
      }
    } catch (error) {
      ThrowError(error);
    } finally {
      setIsLoading(false);
    }
  };

  if (isFatchLoading) {
    return (
      <Layout>
        {/* <div className="flex items-center justify-center min-h-[400px]"> */}
        <FullPageLoader />
        {/* <Spinner className="h-8 w-8 text-primary animate-spin" /> */}
        {/* </div> */}
      </Layout >
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        <MainHeading
          title={id ? "Edit Leave Application" : "Apply for Leave"}
          isBackButtonVisible
          onClick={() => navigate(-1)}
        />
        <Card className="overflow-visible">
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <FormField
                  {...register("leaveType", {
                    ...errorTypes.required("Leave Type"),
                  })}
                  type="select"
                  label="Leave Type"
                  placeholder="Select Leave Type"
                  value={watch("leaveType")}
                  options={[
                    { value: "", label: "Select Leave Type" },
                    ...leaveTypes,
                  ]}
                  error={errors.leaveType?.message as string}
                  required
                />
                {selectedLeaveType !== "" && (
                  <div className="mt-1">
                    {isFatchLoading ? (
                      <p className="text-sm text-slate-400">Loading balance...</p>
                    ) : leaveBalances && availableBalance <= 0 ? (
                      <p className="text-sm text-red-600">
                        Insufficient balance for {selectedLeaveType}
                      </p>
                    ) : leaveBalances ? (
                      <p className="text-sm text-slate-500">
                        Available Balance: <strong>{availableBalance}</strong>
                      </p>
                    ) : null}
                  </div>
                )}
              </div>

              <FormField
                {...register("duration", {
                  ...errorTypes.required("Duration"),
                })}
                type="select"
                label="Duration"
                value={watch("duration")}
                options={[
                  { value: "", label: "Select Duration" },
                  { value: "FULL_DAY", label: "Full Day" },
                  { value: "FIRST_HALF", label: "First Half" },
                  { value: "SECOND_HALF", label: "Second Half" },
                ]}
                error={errors.duration?.message as string}
                required
              />
              <FormField
                {...register("startDate", {
                  ...errorTypes.required("Start Date"),
                  validate: (value: string) => {
                    if (!value) return true;
                    const minDate = moment().subtract(1, "month").startOf("month").format("YYYY-MM-DD");
                    if (value < minDate) {
                      return `Start Date cannot be earlier than last month (${moment(minDate).format("DD-MM-YYYY")})`;
                    }
                    const currentYearEnd = `${new Date().getFullYear()}-12-31`;
                    if (value > currentYearEnd) {
                      return `Start Date cannot exceed ${new Date().getFullYear()} (current year)`;
                    }
                    return true;
                  },
                })}
                type="date"
                label="Start Date"
                min={moment().subtract(1, "month").startOf("month").format("YYYY-MM-DD")}
                max={`${new Date().getFullYear()}-12-31`}
                error={errors.startDate?.message as string}
                required
              />
              {/* {duration=} */}
              <FormField
                {...register("endDate", {
                  ...errorTypes.required("End Date"),
                  validate: (value: string) => {
                    if (!value) return true;
                    if (startDate && value < startDate) {
                      return "End Date cannot be earlier than Start Date";
                    }
                    const currentYearEnd = `${new Date().getFullYear()}-12-31`;
                    if (value > currentYearEnd) {
                      return `End Date cannot exceed ${new Date().getFullYear()} (current year)`;
                    }
                    return true;
                  },
                })}
                type="date"
                label="End Date"
                min={startDate || moment().subtract(1, "month").startOf("month").format("YYYY-MM-DD")}
                max={`${new Date().getFullYear()}-12-31`}
                disabled={
                  duration === "FIRST_HALF" || duration === "SECOND_HALF"
                }
                error={errors.endDate?.message as string}
                required
              />
              <FormField
                {...register("numberOfDays", {
                  ...errorTypes.required("Number of Days"),
                })}
                type="number"
                step="0.5"
                label="Number of Days"
                placeholder="Auto-calculated"
                error={errors.numberOfDays?.message as string}
                readOnly
                required
              />
              <FormField
                {...register("reason", {
                  ...errorTypes.required("Reason for Leave"),
                  ...errorTypes.description
                })}
                type="textarea"
                label="Reason"
                rows={3}
                minLength={10}
                maxLength={500}
                placeholder="Enter reason for leave"
                error={errors.reason?.message as string}
                required
              />
            </div>

            <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-100">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate(-1)}
                disabled={isLoading}

              >
                Cancel
              </Button>
              <Button type="submit" disabled={isLoading}>
                {isLoading
                  ? "Submitting..."
                  : id
                    ? "Update Leave"
                    : "Apply Leave"}
              </Button>
            </div>
          </form>
        </Card>
      </div>
    </Layout>
  );
};

export default AddLeave;
